import type { GeneratedAsset } from '@pulsify/shared/dto'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileImage,
  Link2,
  Loader2,
  Trash2,
} from 'lucide-react'
import { type KeyboardEvent, type ReactNode, useState } from 'react'
import { toast } from 'sonner'
import { useDeleteGeneratedAsset } from '~/entities/generated-asset/model/use-generated-assets'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { cn } from '~/shared/lib/utils'
import { DESTRUCTIVE_OUTLINE_BUTTON } from '~/shared/styles/buttons'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/ui/alert-dialog'
import { Button } from '~/shared/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '~/shared/ui/dialog'
import {
  assetTitle,
  CHECKERBOARD,
  downloadFilename,
  fileTypeLabel,
  formatBytes,
  formatCreatedAt,
  formatDimensions,
  isImageAsset,
  toolLabel,
} from '../lib/asset-info'

interface AssetDetailDialogProps {
  businessId: string
  assets: GeneratedAsset[]
  index: number
  onIndexChange: (index: number) => void
  onClose: () => void
  onDeleted: (assetId: string) => void
}

export function AssetDetailDialog({
  businessId,
  assets,
  index,
  onIndexChange,
  onClose,
  onDeleted,
}: AssetDetailDialogProps) {
  const asset = index >= 0 ? assets[index] : undefined
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const deleteAsset = useDeleteGeneratedAsset(businessId)

  const hasPrevious = index > 0
  const hasNext = index >= 0 && index < assets.length - 1

  function handleKeyDown(event: KeyboardEvent) {
    if (confirmOpen) return
    if (event.key === 'ArrowLeft' && hasPrevious) {
      event.preventDefault()
      onIndexChange(index - 1)
    } else if (event.key === 'ArrowRight' && hasNext) {
      event.preventDefault()
      onIndexChange(index + 1)
    }
  }

  async function handleDownload(target: GeneratedAsset) {
    setDownloading(true)
    try {
      const response = await fetch(target.url)
      if (!response.ok) throw new Error(String(response.status))
      const objectUrl = URL.createObjectURL(await response.blob())
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = downloadFilename(target)
      link.click()
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
    } catch {
      toast.error(m.mediaLibraryDownloadFailed())
      window.open(target.url, '_blank', 'noopener,noreferrer')
    } finally {
      setDownloading(false)
    }
  }

  async function handleCopyLink(target: GeneratedAsset) {
    try {
      await navigator.clipboard.writeText(target.url)
      toast.success(m.mediaLibraryLinkCopied())
    } catch {
      toast.error(m.mediaLibraryCopyFailed())
    }
  }

  async function handleDelete(target: GeneratedAsset) {
    try {
      await deleteAsset.mutateAsync(target.id)
      toast.success(m.mediaLibraryDeleted())
      setConfirmOpen(false)
      onDeleted(target.id)
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
    }
  }

  const title = asset ? assetTitle(asset) : ''
  const dimensions = asset ? formatDimensions(asset) : null

  return (
    <>
      <Dialog open={!!asset} onOpenChange={(open) => !open && onClose()}>
        <DialogContent
          onKeyDown={handleKeyDown}
          className={cn(
            'flex flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl md:grid md:grid-cols-[minmax(0,1fr)_20rem]',
            'max-sm:top-0 max-sm:left-0 max-sm:h-dvh max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0',
          )}
        >
          {asset && (
            <>
              <div
                className={cn(
                  'relative flex min-h-0 flex-1 items-center justify-center p-4 sm:p-6 md:h-[min(80vh,44rem)]',
                  CHECKERBOARD,
                )}
              >
                {isImageAsset(asset) ? (
                  <img
                    key={asset.id}
                    src={asset.url}
                    alt={title}
                    className="max-h-full max-w-full rounded-md object-contain shadow-sm max-md:max-h-[55dvh]"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <FileImage className="size-12" />
                    <span className="text-sm font-medium">{fileTypeLabel(asset.mimeType)}</span>
                  </div>
                )}
                {assets.length > 1 && (
                  <>
                    <NavButton
                      side="left"
                      label={m.mediaLibraryPrevious()}
                      disabled={!hasPrevious}
                      onClick={() => onIndexChange(index - 1)}
                    >
                      <ChevronLeft />
                    </NavButton>
                    <NavButton
                      side="right"
                      label={m.mediaLibraryNext()}
                      disabled={!hasNext}
                      onClick={() => onIndexChange(index + 1)}
                    >
                      <ChevronRight />
                    </NavButton>
                  </>
                )}
              </div>

              <div className="flex flex-col gap-5 overflow-y-auto border-t p-5 md:border-t-0 md:border-l">
                <div className="space-y-2 pr-8">
                  <span className="inline-flex items-center rounded-full bg-brand-subtle px-2 py-0.5 text-xs font-medium text-brand-subtle-foreground">
                    {toolLabel(asset.tool)}
                  </span>
                  <DialogTitle className="text-xl leading-snug break-words">{title}</DialogTitle>
                  <DialogDescription className="sr-only">
                    {m.mediaLibraryDetailsDescription()}
                  </DialogDescription>
                  {assets.length > 1 && (
                    <p className="text-xs text-muted-foreground tabular-nums">
                      {m.mediaLibraryPosition({
                        current: String(index + 1),
                        total: String(assets.length),
                      })}
                    </p>
                  )}
                </div>

                <dl className="divide-y rounded-lg border text-sm">
                  <InfoRow label={m.mediaLibraryInfoCreated()}>
                    {formatCreatedAt(asset.createdAt, 'long')}
                  </InfoRow>
                  <InfoRow label={m.mediaLibraryInfoTool()}>{toolLabel(asset.tool)}</InfoRow>
                  {dimensions && (
                    <InfoRow label={m.mediaLibraryInfoDimensions()}>{dimensions}</InfoRow>
                  )}
                  <InfoRow label={m.mediaLibraryInfoSize()}>{formatBytes(asset.bytes)}</InfoRow>
                  <InfoRow label={m.mediaLibraryInfoType()}>
                    <span title={asset.mimeType}>{fileTypeLabel(asset.mimeType)}</span>
                  </InfoRow>
                </dl>

                <div className="mt-auto grid gap-2">
                  <Button onClick={() => handleDownload(asset)} disabled={downloading}>
                    {downloading ? <Loader2 className="animate-spin" /> : <Download />}
                    {m.mediaLibraryDownload()}
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" onClick={() => handleCopyLink(asset)}>
                      <Link2 />
                      {m.mediaLibraryCopyLink()}
                    </Button>
                    <Button variant="outline" asChild>
                      <a href={asset.url} target="_blank" rel="noreferrer">
                        <ExternalLink />
                        <span className="truncate">{m.mediaLibraryOpenOriginal()}</span>
                      </a>
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    className={DESTRUCTIVE_OUTLINE_BUTTON}
                    onClick={() => setConfirmOpen(true)}
                  >
                    <Trash2 />
                    {m.mediaLibraryDelete()}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen && !!asset} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.mediaLibraryDeleteConfirmTitle({ title })}</AlertDialogTitle>
            <AlertDialogDescription>
              {m.mediaLibraryDeleteConfirmDescription()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteAsset.isPending}
              className={DESTRUCTIVE_OUTLINE_BUTTON}
              onClick={(event) => {
                event.preventDefault()
                if (asset) handleDelete(asset)
              }}
            >
              {deleteAsset.isPending && <Loader2 className="animate-spin" />}
              {m.delete()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-3 py-2.5">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right font-medium break-words">{children}</dd>
    </div>
  )
}

function NavButton({
  side,
  label,
  disabled,
  onClick,
  children,
}: {
  side: 'left' | 'right'
  label: string
  disabled: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <Button
      type="button"
      variant="secondary"
      size="icon"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'absolute top-1/2 -translate-y-1/2 rounded-full shadow-sm disabled:opacity-0',
        side === 'left' ? 'left-3' : 'right-3',
      )}
    >
      {children}
    </Button>
  )
}
